import { useState } from 'react';
import { usePreferences } from '../../context/usePreferences';
import {
  DEFAULT_FOLLOWING_ENABLED,
  DEFAULT_SECTIONS,
} from '../../context/AppStateProvider';
import { useDragReorder } from './useDragReorder';
import './BottomSheet.css';

// Full catalogue of sections a user can add to My News.
const ALL_SECTIONS = [
  'Business',
  'Crime',
  'Culture',
  'Environment',
  'Health',
  'Ireland',
  'Life-style',
  'Opinion',
  'Podcasts',
  'Politics',
  'Property',
  'Science',
  'Sport',
  'Technology',
  'World',
];

export function BottomSheet() {
  const { isOpen } = usePreferences();

  if (!isOpen) return null;

  // Mounted only while open, so the draft re-initialises from the saved
  // sections on every open.
  return <BottomSheetContent />;
}

function BottomSheetContent() {
  const { close, sections, setSections, followingEnabled, setFollowingEnabled } =
    usePreferences();

  // Draft edits stay local; the app only updates when "Done" commits them.
  // Who is followed is not the sheet's business — that lives in the
  // Following tab itself; the sheet only decides whether the tab exists.
  const [draftSections, setDraftSections] = useState(sections);
  const [draftFollowing, setDraftFollowing] = useState(followingEnabled);
  const { listRef, dragIndex, handleProps, rowStyle } = useDragReorder(
    draftSections,
    setDraftSections,
  );

  const addSection = (section) => setDraftSections([...draftSections, section]);
  const removeSection = (section) =>
    setDraftSections(draftSections.filter((s) => s !== section));

  const commit = () => {
    setSections(draftSections);
    setFollowingEnabled(draftFollowing);
    close();
  };

  const resetToDefault = () => {
    setDraftSections(DEFAULT_SECTIONS);
    setDraftFollowing(DEFAULT_FOLLOWING_ENABLED);
  };

  const availableSections = ALL_SECTIONS.filter(
    (section) => !draftSections.includes(section),
  );

  return (
    <div
      className="bottom-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Customise your feed"
    >
      <div className="bottom-sheet__backdrop" onClick={close} aria-hidden="true" />
      <div className="bottom-sheet__sheet">
        <div className="bottom-sheet__handle" aria-hidden="true">
          <span className="bottom-sheet__handle-bar" />
        </div>

        <div className="bottom-sheet__header">
          <h2 className="bottom-sheet__title">Customise your feed</h2>
          <button
            type="button"
            className="bottom-sheet__close"
            onClick={close}
            aria-label="Close"
          >
            <CrossIcon />
          </button>
        </div>

        <p className="bottom-sheet__subtitle">
          Add or remove sections of interest, drag to reorder.
        </p>

        <div className="bottom-sheet__content">
          <section className="bottom-sheet__section">
            <h3 className="bottom-sheet__section-title">Your Sections</h3>
            <ul className="bottom-sheet__rows" ref={listRef}>
              <li className="bottom-sheet__row">
                <span
                  className="bottom-sheet__drag bottom-sheet__drag--locked"
                  aria-hidden="true"
                >
                  <DragIcon />
                </span>
                <span className="bottom-sheet__row-label">Recommended</span>
                <span
                  className="bottom-sheet__locked"
                  aria-label="Recommended cannot be removed"
                >
                  <LockIcon />
                </span>
              </li>

              {/* Following is a fixed tab like Recommended, but optional:
                  the switch shows or hides it. Who is followed is managed
                  inside the tab itself. */}
              <li className="bottom-sheet__row">
                <span
                  className="bottom-sheet__drag bottom-sheet__drag--locked"
                  aria-hidden="true"
                >
                  <DragIcon />
                </span>
                <span className="bottom-sheet__row-label">
                  Following
                  <span className="bottom-sheet__row-hint">
                    A feed from columnists you follow
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draftFollowing}
                  aria-label="Show the Following tab"
                  className="bottom-sheet__switch"
                  onClick={() => setDraftFollowing(!draftFollowing)}
                />
              </li>

              {draftSections.map((section, index) => (
                <li
                  key={section}
                  className={
                    index === dragIndex
                      ? 'bottom-sheet__row bottom-sheet__row--dragging'
                      : 'bottom-sheet__row'
                  }
                  data-reorder-index={index}
                  style={rowStyle(index)}
                >
                  <button
                    type="button"
                    className="bottom-sheet__drag"
                    aria-label={`Reorder ${section}, position ${index + 2} of ${draftSections.length + 1}. Use the arrow keys to move it.`}
                    {...handleProps(index)}
                  >
                    <DragIcon />
                  </button>
                  <span className="bottom-sheet__row-label">{section}</span>
                  <button
                    type="button"
                    className="bottom-sheet__remove"
                    onClick={() => removeSection(section)}
                    aria-label={`Remove ${section}`}
                  >
                    <CrossIcon />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="bottom-sheet__section">
            <h3 className="bottom-sheet__section-title">Add Sections</h3>
            <ul className="bottom-sheet__pills">
              {availableSections.map((section) => (
                <li key={section}>
                  <button
                    type="button"
                    className="bottom-sheet__pill"
                    onClick={() => addSection(section)}
                  >
                    {section}
                    <PlusIcon />
                  </button>
                </li>
              ))}
            </ul>
          </section>

        </div>

        <div className="bottom-sheet__footer">
          <button
            type="button"
            className="bottom-sheet__reset"
            onClick={resetToDefault}
          >
            Reset to default
          </button>
          <button type="button" className="bottom-sheet__done" onClick={commit}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function DragIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
      <circle cx="5" cy="3" r="1.5" />
      <circle cx="11" cy="3" r="1.5" />
      <circle cx="5" cy="8" r="1.5" />
      <circle cx="11" cy="8" r="1.5" />
      <circle cx="5" cy="13" r="1.5" />
      <circle cx="11" cy="13" r="1.5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
      <path d="M4 7V5a4 4 0 0 1 8 0v2h.5A1.5 1.5 0 0 1 14 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5v-5A1.5 1.5 0 0 1 3.5 7H4zm1.5 0h5V5a2.5 2.5 0 0 0-5 0v2z" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <path
        d="M3 3l10 10M13 3L3 13"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M8 3v10M3 8h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
